
import { useState, useEffect } from 'react';
import { useCart } from '../../hooks/useCart';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { fundWallet } from '../../services/wallet';
import { auth, db } from '../../lib/firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import { getCashbackSettings, calculateCashbackForAmount } from '../../services/settings';
import { creditWallet, getWallet, withdraw } from '../../services/wallet';
import { createOrdersSplitBySeller } from '../../services/orders';
import { orderReceivedEmail } from '../../services/email';
import { getUserProfile } from '../../services/auth';
import { createIdempotencyKey } from '../../utils/idempotency';

// Prefer same-origin API; fallback to env or localhost
const API_BASE = (typeof window !== 'undefined' ? window.location.origin : '') ||
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ||
  'http://localhost:5555';

const CheckoutPage = () => {
  const { cartItems: rawCartItems, getTotalPrice, clearCart } = useCart();
  // Narrow type: items from Firestore always have listingId + sellerId; filter satisfies orders.ts requirement
  const cartItems = rawCartItems.filter(
    (item): item is typeof item & { listingId: string; sellerId: string } =>
      typeof item.listingId === 'string' && typeof item.sellerId === 'string'
  );
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);
  const [cashbackInfo, setCashbackInfo] = useState<{ amount: number; enabled: boolean } | null>(null);
  const [walletError, setWalletError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [walletLive, setWalletLive] = useState<{ balance: number; escrowBalance: number; totalEarnings: number } | null>(null);

  // 页面加载时滚动到顶部
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Subscribe to wallet doc for live balance during checkout
  useEffect(() => {
    const unsubAuth = auth.onAuthStateChanged((user) => {
      if (!user) { setWalletLive(null); return; }
      const unsubWallet = onSnapshot(doc(db, 'wallets', user.uid), (snap) => {
        if (snap.exists()) {
          const data = snap.data() as any;
          setWalletLive({
            balance: data.balance || 0,
            escrowBalance: data.escrowBalance || 0,
            totalEarnings: data.totalEarnings || 0,
          });
        }
      });
      return () => unsubWallet();
    });
    return () => unsubAuth();
  }, []);

  useEffect(() => {
    async function previewCashback() {
      const settings = await getCashbackSettings();
      const total = getTotalPrice();
      const cb = calculateCashbackForAmount(total, settings);
      setCashbackInfo({ amount: cb, enabled: settings.enabled });
    }
    previewCashback();
  }, [getTotalPrice]);


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setWalletError(null);
    setIsProcessing(true);

    try {
      const user = auth.currentUser;
      if (!user) {
        setIsProcessing(false);
        setWalletError('You need to sign in to complete checkout.');
        return;
      }

      const total = getTotalPrice();
      // Prefer live wallet balance, fallback to one-off fetch
      let currentBalance = walletLive?.balance;
      if (typeof currentBalance !== 'number') {
        const w = await getWallet(user.uid);
        currentBalance = w.balance || 0;
      }
      if ((currentBalance || 0) < total) {
        setIsProcessing(false);
        setWalletError('Not enough funds. Please go to Wallet to deposit.');
        return;
      }

      // Generate payment reference up front
      const paymentReference = `WALLET-${createIdempotencyKey()}`;

      // Debit buyer wallet for total (label as purchase)
      await withdraw(user.uid, total, { description: `Purchase - escrow hold (₦${total.toLocaleString()})`, reference: paymentReference });

      // Create per-seller orders and hold funds in seller escrow
      const { ordersBySeller } = await createOrdersSplitBySeller({
        buyerId: user.uid,
        cartItems,
        paymentReference,
      });
      try {
        const buyerProfile = await getUserProfile(user.uid);
        const buyerName = buyerProfile.fullName || undefined;
        for (const pair of ordersBySeller) {
          const sellerProfile = await getUserProfile(pair.sellerId);
          if (sellerProfile.email) {
            await orderReceivedEmail(sellerProfile.email, pair.orderId, buyerName);
          }
        }
      } catch (e) {
        console.warn('Failed to send order-received emails', e);
      }

      // Apply cashback credit to buyer wallet
      try {
        const settings = await getCashbackSettings();
        const cashback = calculateCashbackForAmount(total, settings);
        if (settings.enabled && cashback > 0) {
          await creditWallet(user.uid, cashback, `Cashback on purchase of ₦${total.toLocaleString()}`);
        }
      } catch (err) {
        console.error('Failed to apply cashback:', err);
      }

      clearCart();
      setOrderComplete(true);
      navigate('/orders');
    } catch (err) {
      console.error('Checkout failed:', err);
      const message = (err as any)?.message ?? '';
      if (String(message).includes('ORDER_CREATION_IN_PROGRESS')) {
        setErrorCode('ORDER_CREATION_IN_PROGRESS');
        setWalletError('Your order is already being created. Please wait a moment or go to Orders to view status.');
      } else {
        setErrorCode(null);
        setWalletError('Something went wrong while processing your payment. Please try again.');
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePaystackPay = async () => {
    setWalletError(null);
    setIsProcessing(true);
    try {
      const user = auth.currentUser;
      if (!user) { setWalletError('You need to sign in to checkout.'); setIsProcessing(false); return; }
      if (!user.email) { setWalletError('A verified email is required for Paystack payment.'); setIsProcessing(false); return; }
      const paystackKey = import.meta.env.VITE_PAYSTACK_PUBLIC_KEY as string;
      if (!paystackKey) { setWalletError('Paystack is not configured. Please use Flutterwave or Wallet.'); setIsProcessing(false); return; }
      const total = getTotalPrice();
      const ref = `CM-ORDER-PS-${Date.now()}-${user.uid}`;
      const handler = (window as any).PaystackPop.setup({
        key: paystackKey,
        email: user.email,
        amount: Math.round(total * 100), // kobo
        currency: 'NGN',
        ref,
        callback: function(response: any) {
          (async () => {
            try {
              const reference = response.reference || ref;
              const verifyResp = await fetch(`${API_BASE}/api/paystack-verify?reference=${encodeURIComponent(reference)}`);
              const data = await verifyResp.json();
              const txData = data?.data;
              if (!verifyResp.ok) {
                setWalletError(`Payment verification failed (server error). Your money was NOT lost — contact support with reference: ${reference}`);
                setIsProcessing(false);
                return;
              }
              if (txData?.status !== 'success') {
                setWalletError(`Payment not confirmed by Paystack (status: ${txData?.status}). Contact support with reference: ${reference}`);
                setIsProcessing(false);
                return;
              }
              const verifiedAmount = Number(txData?.amount) / 100;
              const creditAmount = verifiedAmount > 0 ? verifiedAmount : total;
              await fundWallet(user.uid, creditAmount, 'paystack', reference);
              const { ordersBySeller } = await createOrdersSplitBySeller({ buyerId: user.uid, cartItems, paymentReference: reference });
              try {
                const buyerProfile = await getUserProfile(user.uid);
                const buyerName = buyerProfile.fullName || undefined;
                for (const pair of ordersBySeller) {
                  const sellerProfile = await getUserProfile(pair.sellerId);
                  if (sellerProfile.email) {
                    await orderReceivedEmail(sellerProfile.email, pair.orderId, buyerName);
                  }
                }
              } catch (e) { console.error('Failed to send order-received emails', e); }
              clearCart();
              setOrderComplete(true);
              navigate('/orders');
            } catch (err) {
              console.error('Paystack checkout failed:', err);
              setWalletError('Something went wrong after payment. Contact support with your reference: ' + ref);
              setIsProcessing(false);
            }
          })();
        },
        onClose: function() { setIsProcessing(false); },
      });
      handler.openIframe();
    } catch (err) {
      console.error('Paystack payment init failed:', err);
      setWalletError('Unable to open Paystack payment.');
      setIsProcessing(false);
    }
  };


  if (orderComplete) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-2xl mx-auto px-4 py-16">
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-check-line text-2xl text-green-600"></i>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Order Confirmed!</h1>
            <p className="text-gray-600 mb-8">Thank you for your purchase. Your order has been successfully placed.</p>
            {cashbackInfo?.enabled && cashbackInfo.amount > 0 && (
              <div className="bg-teal-50 border border-teal-200 text-teal-800 px-4 py-3 rounded mb-6">
                You received a cashback of <span className="font-semibold">₦{cashbackInfo.amount.toLocaleString()}</span> to your wallet.
              </div>
            )}
            <div className="space-y-4">
              <button
                onClick={() => navigate('/shop')}
                className="w-full bg-gray-900 text-white py-3 px-4 rounded-lg hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap font-medium"
              >
                Continue Shopping
              </button>
              <button
                onClick={() => navigate('/')}
                className="w-full border border-gray-300 text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                Back to Home
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="max-w-2xl mx-auto px-4 py-8">
        {cashbackInfo?.enabled && (
          <div className="bg-teal-50 border border-teal-200 text-teal-800 px-4 py-3 rounded mb-6">
            Cashback preview for this order: <span className="font-semibold">₦{(cashbackInfo.amount || 0).toLocaleString()}</span>
          </div>
        )}
        {walletError && (
          <div className="bg-red-100 border border-red-200 text-red-800 px-4 py-3 rounded mb-4">
            <div className="font-medium">{walletError}</div>
            {errorCode === 'ORDER_CREATION_IN_PROGRESS' ? (
              <div className="mt-2">
                <button
                  onClick={() => navigate('/orders')}
                  className="text-sm underline text-red-800 hover:text-red-900"
                >
                  Go to Orders
                </button>
              </div>
            ) : (
              <div className="mt-2 text-sm text-gray-700">
                <span>Current wallet balance: </span>
                <span className="font-medium">₦{(walletLive?.balance ?? 0).toLocaleString()}</span>
                <button
                  onClick={() => navigate('/wallet')}
                  className="ml-2 underline"
                >
                  Go to Wallet
                </button>
              </div>
            )}
          </div>
        )}
        {/* Existing checkout form content continues... */}
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm p-6">
          {/* Minimal placeholder submit to trigger logic */}
          <div className="grid grid-cols-1 gap-3">
            <button
              type="button"
              onClick={handlePaystackPay}
              disabled={isProcessing || cartItems.length === 0}
              className="w-full bg-green-600 text-white py-3 px-4 rounded-lg hover:bg-green-700 transition-colors cursor-pointer whitespace-nowrap font-medium disabled:opacity-60"
            >
              {isProcessing ? 'Processing...' : 'Pay with Paystack'}
            </button>
            <button
              type="submit"
              disabled={isProcessing || cartItems.length === 0}
              className="w-full bg-gray-900 text-white py-3 px-4 rounded-lg hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap font-medium disabled:opacity-60"
            >
              {isProcessing ? 'Processing...' : 'Pay with Wallet Balance'}
            </button>
          </div>
        </form>
      </div>
      {/* Rest of existing JSX unchanged */}
    </div>
  );
};

export default CheckoutPage;
